<script setup lang="ts">
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import Button from "@/stories/buttons/Button/Button.vue";
import DialogFooterActions from "@/stories/dialogs/DialogFooterActions.vue";
import { onMounted, ref } from "vue";
import ButtonToolbar from "@/stories/buttons/ButtonToolbar/ButtonToolbar.vue";
import ButtonToolbarContainer from "@/stories/buttons/ButtonToolbar/ButtonToolbarContainer.vue";
import { useBreakpoints } from "@vueuse/core";
import { BREAKPOINTS } from "@/consts";
import waitFor from "@/helpers/shared/waitFor";

interface Props {
  src: string;
}

defineProps<Props>();

const emit = defineEmits<{
  close: [];
  apply: [string | undefined];
}>();

const dataURL = ref<string>();
const isMaximized = ref(true);
const cropperRef = ref();
const possibleHeight = ref();
const isActive = ref(false);

const breakpoints = useBreakpoints(BREAKPOINTS);
const isTablet = breakpoints.greaterOrEqual("tablet");

type CropChangeParams = {
  canvas: {
    toDataURL: () => string;
  };
};

type SetCoordinatesParams = {
  imageSize: {
    width: number;
    height: number;
  };
  coordinates: {
    width: number;
    height: number;
  };
};

const handleCropChange = ({ canvas }: CropChangeParams) => {
  dataURL.value = canvas.toDataURL();
};

const handleApply = () => {
  emit("apply", dataURL.value);
};

const handleCancel = () => {
  emit("close");
};

const rotate = (angle: number) => {
  cropperRef.value.rotate(angle);
  isMaximized.value = false;
};

const zoomIn = () => {
  cropperRef.value.zoom(2);
};

const zoomOut = () => {
  cropperRef.value.zoom(0.5);
};

const maximize = () => {
  const cropper = cropperRef.value;
  const center = {
    left: cropper.coordinates.left + cropper.coordinates.width / 2,
    top: cropper.coordinates.top + cropper.coordinates.height / 2,
  };
  cropper.setCoordinates([
    ({ imageSize }: SetCoordinatesParams) => ({
      width: imageSize.width,
      height: imageSize.height,
    }),
    ({ coordinates }: SetCoordinatesParams) => ({
      left: center.left - coordinates.width / 2,
      top: center.top - coordinates.height / 2,
    }),
  ]);
  isMaximized.value = true;
};

const minimize = () => {
  const cropper = cropperRef.value;
  const center = {
    left: cropper.coordinates.left + cropper.coordinates.width / 2,
    top: cropper.coordinates.top + cropper.coordinates.height / 2,
  };
  cropper.setCoordinates([
    ({ imageSize }: SetCoordinatesParams) => ({
      width: imageSize.width / 2,
      height: imageSize.height / 2,
    }),
    ({ coordinates }: SetCoordinatesParams) => ({
      left: center.left - coordinates.width / 2,
      top: center.top - coordinates.height / 2,
    }),
  ]);
  isMaximized.value = false;
};

const flip = (x: boolean, y: boolean) => {
  const { image } = cropperRef.value.getResult();
  if (image.transforms.rotate % 180 !== 0) {
    cropperRef.value.flip(!x, !y);
  } else {
    cropperRef.value.flip(x, y);
  }
};

const setPossibleHeight = () => {
  if (isTablet.value) {
    const value = window.innerHeight - 400;
    possibleHeight.value = `${value}px`;
  } else {
    possibleHeight.value = window.innerHeight;
  }
};

onMounted(async () => {
  // On table, we always set the height value by the window height.
  if (isTablet.value) {
    setPossibleHeight();
  }

  // We need a trashold to activate the cropper. First the dialog should be
  // shown properly. After that we can activate the croppper safely.
  await waitFor(200);
  isActive.value = true;

  // It takes time to cropper is running. So wait little bit more to set the
  // cropper to maximum width/height. Otherwise we can not find the reference
  await waitFor(300);
  maximize();
});
</script>

<template>
  <BigDialog @close="$emit('close')">
    <DialogHeader @close="$emit('close')">
      {{ $t("templates.image_editing") }}
    </DialogHeader>

    <div
      class="bg-neutral100 flex flex-col flex-grow justify-center pb-[78px] desktop:pb-0"
    >
      <div class="relative">
        <div
          class="overflow-hidden flex flex-col justify-center max-h-[800px]"
          :style="{ height: possibleHeight }"
        >
          <Cropper
            v-if="isActive"
            class="cropper"
            ref="cropperRef"
            :src="src"
            :canvas="true"
            @change="handleCropChange"
          />
        </div>
        <ButtonToolbarContainer
          class="fixed left-0 right-0 mx-auto bottom-[100px] desktop:bottom-2 desktop:absolute"
        >
          <ButtonToolbar
            icon="far fa-arrows-maximize"
            @click="maximize"
            :isActive="isMaximized"
          />
          <ButtonToolbar
            icon="far fa-crop"
            @click="minimize"
            :isActive="!isMaximized"
          />
          <ButtonToolbar icon="far fa-rotate-left" @click="rotate(-90)" />
          <ButtonToolbar
            v-if="isTablet"
            icon="far fa-rotate-right"
            @click="rotate(90)"
          />
          <ButtonToolbar icon="far fa-magnifying-glass-plus" @click="zoomIn" />
          <ButtonToolbar
            icon="far fa-magnifying-glass-minus"
            @click="zoomOut"
          />
          <ButtonToolbar
            v-if="isTablet"
            icon="far fa-arrows-left-right"
            @click="flip(true, false)"
          />
          <ButtonToolbar
            v-if="isTablet"
            icon="far fa-arrows-up-down"
            @click="flip(false, true)"
          />
        </ButtonToolbarContainer>
      </div>
    </div>

    <DialogFooterActions>
      <Button
        :text="$t('global.cancel')"
        :type="'white'"
        @click="handleCancel"
      />
      <Button :text="$t('global.use')" @click="handleApply" />
    </DialogFooterActions>
  </BigDialog>
</template>

<style lang="scss">
.vue-advanced-cropper__background,
.vue-advanced-cropper__foreground {
  background-color: theme("colors.neutral100") !important;
}
</style>
